import type React from 'react';

import { AdornedInput, Adornment, FormField, Label } from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../models/index';
import { HOMEOWNER_OR_CONDO_ASSOCIATION } from '../../../constants/index';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { CURRENCY_INPUT_PLACEHOLDER } from '../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function HomeownerOrCondoAssociation_v1({ section, setSectionValues }: Props): JSX.Element {
  function onNoPartOfAssociationClick(): void {
    setSectionValues({
      [HOMEOWNER_OR_CONDO_ASSOCIATION.FIELD_NAME.PART_OF_ASSOCIATION]: false,
      [HOMEOWNER_OR_CONDO_ASSOCIATION.FIELD_NAME.PAY_ANY_FEES]: null,
      [HOMEOWNER_OR_CONDO_ASSOCIATION.FIELD_NAME.FEE_AMOUNT]: '',
    });
  }

  function onYesPartOfAssociationClick(): void {
    setSectionValues({
      [HOMEOWNER_OR_CONDO_ASSOCIATION.FIELD_NAME.PART_OF_ASSOCIATION]: true,
    });
  }

  function onNoPayFees(): void {
    setSectionValues({
      [HOMEOWNER_OR_CONDO_ASSOCIATION.FIELD_NAME.PAY_ANY_FEES]: false,
      [HOMEOWNER_OR_CONDO_ASSOCIATION.FIELD_NAME.FEE_AMOUNT]: '',
    });
  }

  function onYesPayFees(): void {
    setSectionValues({
      [HOMEOWNER_OR_CONDO_ASSOCIATION.FIELD_NAME.PAY_ANY_FEES]: true,
    });
  }

  function onFeeAmountChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [HOMEOWNER_OR_CONDO_ASSOCIATION.FIELD_NAME.FEE_AMOUNT]: value });
    setSectionValues({
      [HOMEOWNER_OR_CONDO_ASSOCIATION.FIELD_NAME.MONTHLY_FEE_AMOUNT]: (Number(value) / 12).toFixed(2),
    });
  }

  return (
    <FormField
      label={<Label size="lg">Is the property part of a homeowners or condo association?</Label>}
      control={
        <YesAndNoExpansion
          yesHandler={onYesPartOfAssociationClick}
          noHandler={onNoPartOfAssociationClick}
          yesActive={section.getFieldValue(HOMEOWNER_OR_CONDO_ASSOCIATION.FIELD_NAME.PART_OF_ASSOCIATION)}
        >
          <FormField
            label={
              <Label>Will the tenant pay any homeowners or condo association fees in addition to base rent?</Label>
            }
            control={
              <YesAndNoExpansion
                yesHandler={onYesPayFees}
                noHandler={onNoPayFees}
                yesActive={section.getFieldValue(HOMEOWNER_OR_CONDO_ASSOCIATION.FIELD_NAME.PAY_ANY_FEES)}
              >
                <FormField
                  label={<Label id="Estimated-association-fees">Estimated association fees</Label>}
                  control={
                    <AdornedInput
                      inputId="Adorned-input-estimated-association-fees"
                      leftAdornment={<Adornment>$</Adornment>}
                      rightAdornment={<Adornment>per year</Adornment>}
                      input={
                        <CurrencyInput
                          aria-labelledby="Estimated-association-fees"
                          placeholder={CURRENCY_INPUT_PLACEHOLDER}
                          onChange={onFeeAmountChange}
                          onBlur={onFeeAmountChange}
                          value={section.getFieldValue(HOMEOWNER_OR_CONDO_ASSOCIATION.FIELD_NAME.FEE_AMOUNT)}
                        />
                      }
                    />
                  }
                />
              </YesAndNoExpansion>
            }
          />
        </YesAndNoExpansion>
      }
    />
  );
}
